import type { ParsedBrickCell } from "./AsciiBrickMapParser.js";

/**
 * 생성된 벽돌 셀의 일부를 특수 벽돌(shield/armor/regen)로 마킹 — 순수(Phaser 비의존), 결정론(rng 주입).
 *
 * `markMysteryBricks`(mystery) 뒤에 **체인** 호출한다(이미 비-normal이 된 셀은
 * 자동 제외 — 한 셀이 mystery이면서 shield/armor/regen일 수 없다). normal 블록의 `kind`만 바꾸고
 * `hpMultiplier`(=칸수)는 보존한다(HP·골드 배수는 spawn 단계에서 kind별 factor로 합성). 입력 배열은
 * 변경하지 않고 새 배열을 반환한다. mystery와 다른 salt seed로 위치를 독립 결정론화한다.
 *
 * 월드별 등장 비율은 data/specialBrickRules.tsv에서 온 임계 규칙으로 결정한다:
 * 월드 1은 전부 미등장, 월드 2부터 shield가 시작되고 이후 armor/regen이 순차 해금된다.
 */

/** worldMin 임계 배열 ↔ shield/armor/regen 셀별 확률(%, 0~100) zip. (mysteryMulValues와 동형 array-column.) */
export interface SpecialBrickRules {
  /** 오름차순 월드 임계값(예 [1,2,3,4,5]). 해당 월드 이하의 가장 큰 임계 규칙을 선택. */
  readonly worldMin: readonly number[];
  /** 임계별 shield 셀 마킹 확률(%, 0~100). worldMin과 동일 인덱스. */
  readonly shieldWeight: readonly number[];
  /** 임계별 armor 셀 마킹 확률(%, 0~100). worldMin과 동일 인덱스. */
  readonly armorWeight: readonly number[];
  /** 임계별 regen 셀 마킹 확률(%, 0~100). worldMin과 동일 인덱스. */
  readonly regenWeight: readonly number[];
}

export interface SpecialBrickChances {
  /** 셀별 shield 마킹 확률(0~1). */
  readonly shieldChance: number;
  /** 셀별 armor 마킹 확률(0~1). */
  readonly armorChance: number;
  /** 셀별 regen 마킹 확률(0~1). */
  readonly regenChance: number;
}

/**
 * 해당 월드의 shield/armor/regen 셀별 확률(0~1)을 임계 규칙에서 해석. `worldNo >= worldMin[i]`인 가장 큰 i를
 * 선택(임계 미만이면 첫 규칙 — 보통 weight 0). weight(%)를 0~1로 환산해 반환.
 */
export function resolveSpecialBrickWeights(
  worldNo: number,
  rules: SpecialBrickRules,
): SpecialBrickChances {
  let idx = 0;
  for (let i = 0; i < rules.worldMin.length; i++) {
    if (worldNo >= rules.worldMin[i]!) idx = i;
  }
  return {
    shieldChance: (rules.shieldWeight[idx] ?? 0) / 100,
    armorChance: (rules.armorWeight[idx] ?? 0) / 100,
    regenChance: (rules.regenWeight[idx] ?? 0) / 100,
  };
}

/** 방어막 타격 횟수 챕터 스케일 — 초기 base(2) → lateChapter 이상에서 late(3) (기획 §5). */
export interface ShieldHitsConfig {
  readonly base: number;
  readonly lateChapter: number;
  readonly late: number;
}

export function resolveShieldHits(chapter: number, cfg: ShieldHitsConfig): number {
  return chapter >= cfg.lateChapter ? cfg.late : cfg.base;
}

export interface MarkSpecialOptions {
  readonly shieldChance: number;
  readonly armorChance: number;
  readonly regenChance: number;
  /** 챕터당 shield 벽돌 최대 개수. */
  readonly shieldMax: number;
  /** 챕터당 armor 벽돌 최대 개수. */
  readonly armorMax: number;
  /** 챕터당 regen 벽돌 최대 개수. */
  readonly regenMax: number;
}

/**
 * normal 셀을 순회하며 단일 roll로 shield/armor/regen/그대로를 분배(상한별 max). [0,1) 구간을
 * shield → armor → regen → 일반으로 분할해 한 번의 rng()로 결정(결정론·가독). 상한 도달 종류는 다음 구간으로 흡수.
 * rng는 seed 파생(makeRng)으로 주입해 같은 (cells, rng seed) → 같은 결과.
 */
export function markSpecialBricks(
  cells: readonly ParsedBrickCell[],
  rng: () => number,
  opts: MarkSpecialOptions,
): ParsedBrickCell[] {
  const { shieldChance, armorChance, regenChance, shieldMax, armorMax, regenMax } = opts;
  let shieldCount = 0;
  let armorCount = 0;
  let regenCount = 0;
  return cells.map((c) => {
    if (c.kind !== "normal") return { ...c };
    const r = rng();
    if (r < shieldChance && shieldCount < shieldMax) {
      shieldCount += 1;
      return { ...c, kind: "shield" as const };
    }
    if (r < shieldChance + armorChance && armorCount < armorMax) {
      armorCount += 1;
      return { ...c, kind: "armor" as const };
    }
    if (r < shieldChance + armorChance + regenChance && regenCount < regenMax) {
      regenCount += 1;
      return { ...c, kind: "regen" as const };
    }
    return { ...c };
  });
}
